import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// prettier-ignore
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { CTChartConfigurationService } from '@cloud-twin-v2/cloud-twin-shared/chart/services/ct-chart-configuration.service';
import { DashboardCartAndSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/dashboard-cart-and-settings';
import { IWidgetSettings } from '@cloud-twin-v2/cloud-twin-shared/filtrable-items/widget-settings';
import {
  ButtonTogglerItemSettings,
  ButtonTogglerSettings,
} from '@common-modules/shared-component/button-toggler/button-toggler-settings';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { InjectContentHostDirective } from '@common-modules/shared/directives/inject-content-host.directive';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { IFiltrableItemDto } from '@common-modules/shared/model/filtrable-items/filtrable-item.dto';
import { DynamicRenderizerComponentService } from '@common-modules/shared/services/dynamic-renderizer-component.service';
import { LogService } from '@common-modules/shared/wlm-log/log.service';
import { map, Observable, of, ReplaySubject, startWith, switchMap } from 'rxjs';
import { BaseDashboardSettingsComponent } from '../core/base-dashboard-settings.component';
import { CreateDashboardWidgetFormComponent } from '../create-dashboard-widget-form/create-dashboard-widget-form.component';
import { ctDashboardSettingsComponentByType } from '../ct-dashboard-mappings';
import { DashboardChartSettingsComponent } from '../dashboard-chart-settings/dashboard-chart-settings.component';

const COMPONENT_SELECTOR = 'ct-dashboard-child-config';

@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './dashboard-child-config.component.html',
  styleUrl: './dashboard-child-config.component.scss',
  standalone: false,
})
export class DashboardChildConfigComponent implements OnInit {
  private _settingsComponent: BaseDashboardSettingsComponent;
  get settingsComponent(): BaseDashboardSettingsComponent {
    return this._settingsComponent;
  }
  @ViewChild(DashboardChartSettingsComponent) set settingsComponent(
    value: BaseDashboardSettingsComponent
  ) {
    this._settingsComponent = value;
    if (this._settingsComponent) {
      this._settingsComponentBound$.next();
    }
  }

  private _cartAndSettings: DashboardCartAndSettings;
  get cartAndSettings(): DashboardCartAndSettings {
    return this._cartAndSettings;
  }
  @Input() set cartAndSettings(value: DashboardCartAndSettings) {
    this._cartAndSettings = value;
    this.instanceSettingsComponent();
  }

  private _newVisibleItems: IFiltrableItemDto[];
  public get newVisibleItems(): IFiltrableItemDto[] {
    return this._newVisibleItems;
  }
  @Input() set newVisibleItems(value: IFiltrableItemDto[]) {
    if (value) {
      this._newVisibleItems = value;
      this.syncVisibleItemsWithNewVisibleItems(value);
    }
  }

  private _newItems: IFiltrableItemDto[];
  public get newItems(): IFiltrableItemDto[] {
    return this._newItems;
  }
  @Input() set newItems(value: IFiltrableItemDto[]) {
    this._newItems = value;
    if (!value || !this.cartAndSettings) return;
    this.syncItemsWithNewItems(value);
  }

  @Output() isAllValid = new EventEmitter<boolean>();

  @ViewChild(MatButtonToggleGroup) toogleGroup: MatButtonToggleGroup;
  @ViewChild(CreateDashboardWidgetFormComponent) cartComponent: CreateDashboardWidgetFormComponent;
  @ViewChild(InjectContentHostDirective) set queryHostDirective(
    hostDirective: InjectContentHostDirective
  ) {
    this._hostDirective = hostDirective;
    if (this._hostDirective) {
      this.instanceSettingsComponent();
    }
  }

  buttonTogglerSettings: ButtonTogglerSettings;
  toggleViewMode: 'source' | 'settings' = 'source';
  sourceMode = 'source';
  settingsMode = 'settings';
  isReadOnly: boolean;
  private _isValidSource = false;

  private _isValidSettings = false;
  private _isAllValid = false;
  private _updatedSettings: IWidgetSettings;

  private _hostDirective: InjectContentHostDirective;
  private _settingsComponentBound$ = new ReplaySubject<void>(1);
  readonly T_SCOPE = `${AppModules.RightPanel}.${COMPONENT_SELECTOR}`;
  private readonly _dynamicRenderizerService = inject(DynamicRenderizerComponentService);
  private readonly _logService = inject(LogService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _chartConfigurationService = inject(CTChartConfigurationService);

  readonly hasChangesSettings$ = this._settingsComponentBound$.pipe(
    switchMap(() => this._settingsComponent.hasChanges$),
    startWith(false)
  );

  readonly isValidSettings$ = this._settingsComponentBound$.pipe(
    switchMap(() => this._settingsComponent.isValid$),
    startWith(false)
  );

  constructor() {
    this.buttonTogglerSettings = this.buildButtonTogglerSettings();
  }

  ngOnInit(): void {
    this.isValidSettings$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isValidSettings) => {
        this._isValidSettings = isValidSettings;
        this.checkIsAllValid();
      });
  }

  onValidSourceChange(isValidSource: boolean): void {
    this._isValidSource = isValidSource;
    this.checkIsAllValid();
  }

  private instanceSettingsComponent(): void {
    if (this._hostDirective && this.cartAndSettings) {
      const widgetType = this.cartAndSettings.widgetType;
      const settingsComponent = ctDashboardSettingsComponentByType.get(widgetType);

      if (!settingsComponent) {
        this._logService.error({
          msg: `Could not find settings component for widget type ${widgetType}`,
        });
        return;
      }

      this._dynamicRenderizerService.injectComponentIntoViewContainer(
        settingsComponent,
        this._hostDirective.viewContainerRef,
        (component) => this.bindInnerComponent(component)
      );
    }
  }

  private bindInnerComponent(component: BaseDashboardSettingsComponent): void {
    component.detectChanges(); // Prevented NG0100
    setTimeout(() => {
      this._settingsComponent = component;
      this._settingsComponent.setInitialModel(this.cartAndSettings?.widgetSettings);
      this._settingsComponentBound$.next();
      this._settingsComponentBound$.complete();
    });
  }

  buildCartAndSettings(): Observable<DashboardCartAndSettings> {
    if (!this._isAllValid) {
      return of(null);
    }

    const cartAndSettings$ = this.cartComponent.buildCartAndSettings();
    const widgetSettings = this._settingsComponent.getWidgetSettings();

    if (!widgetSettings) {
      return of(null);
    }

    return cartAndSettings$.pipe(
      map((cartAndSettings) => {
        const clonedCartAndSettings = globalUtilsHelper.clone(cartAndSettings, true);
        clonedCartAndSettings.widgetSettings = widgetSettings;
        return clonedCartAndSettings;
      })
    );
  }
  private checkIsAllValid(): void {
    this._isAllValid = this._isValidSettings;
    // && this._isValidSource;
    this.isAllValid.emit(this._isAllValid);
  }

  private buildButtonTogglerSettings(): ButtonTogglerSettings {
    return new ButtonTogglerSettings({
      buttons: [
        new ButtonTogglerItemSettings({
          value: 'source',
          labelKey: 'source',
          beforeToggle: this.beforeToggle,
        }),
        new ButtonTogglerItemSettings({
          value: 'settings',
          labelKey: 'settings',
        }),
      ],
    });
  }

  private beforeToggle = (value): Observable<boolean> => of(false);

  onChange(event: MatButtonToggleChange) {
    this.toggleViewMode = event.value;
  }

  private syncVisibleItemsWithNewVisibleItems(value: IFiltrableItemDto[]): void {
    if (this.cartAndSettings) {
      if (!this.cartAndSettings.visibleItems) {
        this.cartAndSettings.visibleItems = [];
      }
      const newKeys = new Set(value.map((item) => item.filtrableType?.key));
      this.cartAndSettings.visibleItems = this.cartAndSettings.visibleItems.filter((item) =>
        newKeys.has(item.filtrableType?.key)
      );
      value.forEach((item) => {
        const key = item.filtrableType?.key;
        if (key && !this.cartAndSettings.visibleItems.some((i) => i.filtrableType?.key === key)) {
          this.cartAndSettings.visibleItems.push(item);
        }
      });
    }
    this._settingsComponent.setInitialModel(this.cartAndSettings.widgetSettings);
    this._settingsComponent.setExtraParams(value);
    this._settingsComponentBound$.next();
    this._settingsComponentBound$.complete();
  }

  private syncItemsWithNewItems(value: IFiltrableItemDto[]): void {
    const newKeys = new Set(value.map((item) => item.filtrableType?.key));

    if (this.cartAndSettings.items) {
      this.cartAndSettings.items = this.cartAndSettings.items.filter((item) =>
        newKeys.has(item.filtrableType?.key)
      );
    } else {
      this.cartAndSettings.items = [];
    }
    if (this.cartAndSettings.visibleItems) {
      this.cartAndSettings.visibleItems = this.cartAndSettings.visibleItems.filter((item) =>
        newKeys.has(item.filtrableType?.key)
      );
    } else {
      this.cartAndSettings.visibleItems = [];
    }

    value.forEach((item) => {
      const key = item.filtrableType?.key;
      if (key) {
        if (!this.cartAndSettings.items.some((i) => i.filtrableType?.key === key)) {
          this.cartAndSettings.items.push(item);
        }
        if (!this.cartAndSettings.visibleItems.some((i) => i.filtrableType?.key === key)) {
          this.cartAndSettings.visibleItems.push(item);
        }
      }
    });

    const valueKeys = new Set(value.map((item) => item.filtrableType?.key));
    this.cartAndSettings.items = this.cartAndSettings.items.filter((item) =>
      valueKeys.has(item.filtrableType?.key)
    );
    this.cartAndSettings.visibleItems = this.cartAndSettings.visibleItems.filter((item) =>
      valueKeys.has(item.filtrableType?.key)
    );
  }
}
